import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Calendar, Clock, Edit2, Loader2, Plus, Trash2, Users, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { appointmentTypesApi, schedulesApi, workersApi } from '../../services/api';

const emptyType = { name: '', duration_minutes: 30, price: '', description: '', prompt_notes: '' };
const emptyWorker = { name: '' };
const emptySchedule = { day_of_week: 0, start_time: '09:00', end_time: '17:00', worker_id: null };

export default function AppointmentSetup() {
  const { t } = useTranslation();
  const [types, setTypes] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editor, setEditor] = useState(null);
  const [form, setForm] = useState({});

  const days = useMemo(() => [
    t('days.monday'), t('days.tuesday'), t('days.wednesday'), t('days.thursday'),
    t('days.friday'), t('days.saturday'), t('days.sunday'),
  ], [t]);

  const load = useCallback(async () => {
    try {
      const [typeResponse, workerResponse, scheduleResponse] = await Promise.all([
        appointmentTypesApi.list(),
        workersApi.list(),
        schedulesApi.list(),
      ]);
      setTypes(typeResponse.data || []);
      setWorkers(workerResponse.data || []);
      setSchedules(scheduleResponse.data || []);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openEditor = (kind, item = null) => {
    setEditor({ kind, item });
    if (kind === 'type') setForm(item ? {
      name: item.name,
      duration_minutes: item.duration_minutes,
      price: item.price ?? '',
      description: item.description || '',
      prompt_notes: item.prompt_notes || '',
    } : emptyType);
    if (kind === 'worker') setForm(item ? { name: item.name } : emptyWorker);
    if (kind === 'schedule') setForm(item ? {
      day_of_week: item.day_of_week,
      start_time: String(item.start_time).slice(0, 5),
      end_time: String(item.end_time).slice(0, 5),
      worker_id: item.worker_id || null,
    } : emptySchedule);
  };

  const save = async () => {
    if ((editor.kind === 'type' || editor.kind === 'worker') && !form.name?.trim()) {
      toast.error(t('settings.nameRequired'));
      return;
    }
    setSaving(true);
    try {
      if (editor.kind === 'type') {
        if (editor.item) await appointmentTypesApi.update(editor.item.id, form);
        else await appointmentTypesApi.create(form);
      } else if (editor.kind === 'worker') {
        if (editor.item) await workersApi.update(editor.item.id, form);
        else await workersApi.create(form.name);
      } else {
        if (editor.item) await schedulesApi.update(editor.item.id, form);
        else await schedulesApi.create(form);
      }
      await load();
      setEditor(null);
      toast.success(t('common.saved'));
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (kind, id) => {
    const confirmationKey = kind === 'type'
      ? 'settings.confirmDeleteType'
      : kind === 'worker' ? 'settings.confirmDeleteWorker' : 'settings.confirmDeleteSchedule';
    if (!window.confirm(t(confirmationKey))) return;
    try {
      if (kind === 'type') await appointmentTypesApi.delete(id);
      else if (kind === 'worker') await workersApi.delete(id);
      else await schedulesApi.delete(id);
      await load();
      toast.success(t('common.deleted'));
    } catch (error) {
      toast.error(error.message);
    }
  };

  const cards = [
    { kind: 'type', title: t('settings.appointmentTypes'), icon: Calendar, items: types, empty: t('settings.noTypes') },
    { kind: 'worker', title: t('settings.workers'), icon: Users, items: workers, empty: t('settings.noWorkers') },
    { kind: 'schedule', title: t('settings.schedules'), icon: Clock, items: schedules, empty: t('settings.noSchedules') },
  ];

  const editorTitle = editor?.kind === 'type'
    ? t(editor.item ? 'appointmentTypes.editType' : 'appointmentTypes.addType')
    : editor?.kind === 'worker'
      ? t(editor.item ? 'workers.editWorker' : 'workers.addWorker')
      : t(editor?.item ? 'schedules.editSchedule' : 'schedules.addSchedule');

  return (
    <>
      <section className="booking-config-card booking-setup">
        <div className="booking-card-title">
          <div><h2>{t('bookingLink.setupTitle')}</h2><p>{t('bookingLink.setupSubtitle')}</p></div>
        </div>
        {loading ? <div className="booking-setup-loading"><Loader2 className="spinner" /></div> : (
          <div className="booking-setup-grid">
            {cards.map(({ kind, title, icon: Icon, items, empty }) => (
              <div className="booking-setup-panel" key={kind}>
                <div className="booking-setup-head">
                  <span><Icon size={18} /></span><h3>{title}</h3>
                  <button className="btn btn-primary btn-sm" onClick={() => openEditor(kind)}><Plus size={15} />{t('common.add')}</button>
                </div>
                <div className="booking-setup-items">
                  {items.length === 0 ? <p>{empty}</p> : items.map((item) => (
                    <div className="booking-setup-item" key={item.id}>
                      <div>
                        <b>{kind === 'schedule' ? days[item.day_of_week] : item.name}</b>
                        {kind === 'type' && <small>{item.duration_minutes} {t('common.minutes')}{item.price ? ` · ₪${item.price}` : ''}</small>}
                        {kind === 'worker' && <small>{item.is_active ? t('common.active') : t('common.inactive')}</small>}
                        {kind === 'schedule' && <small><span dir="ltr">{String(item.start_time).slice(0, 5)} – {String(item.end_time).slice(0, 5)}</span>{item.worker_name ? ` · ${item.worker_name}` : ''}</small>}
                      </div>
                      <div className="booking-setup-actions">
                        <button onClick={() => openEditor(kind, item)} aria-label={t('common.edit')}><Edit2 size={15} /></button>
                        <button onClick={() => remove(kind, item.id)} aria-label={t('common.delete')}><Trash2 size={15} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {editor && (
        <div className="booking-setup-overlay" onMouseDown={() => setEditor(null)}>
          <div className="booking-setup-modal" onMouseDown={(event) => event.stopPropagation()}>
            <div className="booking-setup-modal-head"><h3>{editorTitle}</h3><button onClick={() => setEditor(null)}><X size={19} /></button></div>
            <div className="booking-setup-form">
              {editor.kind === 'type' && <>
                <label>{t('common.name')}<input className="input" value={form.name || ''} onChange={(event) => setForm({ ...form, name: event.target.value })} /></label>
                <div className="booking-setup-form-row">
                  <label>{t('settings.duration')}<input className="input" type="number" min="5" step="5" value={form.duration_minutes} onChange={(event) => setForm({ ...form, duration_minutes: Number(event.target.value) })} /></label>
                  <label>{t('settings.price')}<input className="input" type="number" min="0" value={form.price} onChange={(event) => setForm({ ...form, price: event.target.value })} /></label>
                </div>
                <label>{t('settings.description')}<textarea className="input" rows="3" value={form.description || ''} onChange={(event) => setForm({ ...form, description: event.target.value })} /></label>
              </>}
              {editor.kind === 'worker' && <label>{t('common.name')}<input className="input" value={form.name || ''} onChange={(event) => setForm({ ...form, name: event.target.value })} /></label>}
              {editor.kind === 'schedule' && <>
                <label>{t('settings.dayOfWeek')}<select className="input" value={form.day_of_week} onChange={(event) => setForm({ ...form, day_of_week: Number(event.target.value) })}>{days.map((day, index) => <option value={index} key={day}>{day}</option>)}</select></label>
                <div className="booking-setup-form-row">
                  <label>{t('settings.startTime')}<input className="input" type="time" value={form.start_time} onChange={(event) => setForm({ ...form, start_time: event.target.value })} /></label>
                  <label>{t('settings.endTime')}<input className="input" type="time" value={form.end_time} onChange={(event) => setForm({ ...form, end_time: event.target.value })} /></label>
                </div>
                <label>{t('settings.worker')}<select className="input" value={form.worker_id || ''} onChange={(event) => setForm({ ...form, worker_id: event.target.value ? Number(event.target.value) : null })}><option value="">{t('settings.allWorkers')}</option>{workers.map((worker) => <option value={worker.id} key={worker.id}>{worker.name}</option>)}</select></label>
              </>}
            </div>
            <div className="booking-setup-modal-actions">
              <button className="btn btn-secondary" onClick={() => setEditor(null)}>{t('common.cancel')}</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving && <Loader2 className="spinner" size={16} />}{t('common.save')}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
